import { DOCUMENT, ViewportScroller } from '@angular/common';
import { DestroyRef, EnvironmentProviders, inject, provideEnvironmentInitializer } from '@angular/core';
import { NavigationEnd, NavigationStart, PRIMARY_OUTLET, Router, Scroll, UrlSerializer } from '@angular/router';

// Long enough for a cache-miss query to render the page back to its saved height.
export const RESTORE_TIMEOUT_MS = 2000;
const USER_SCROLL_EVENTS = ['wheel', 'touchstart', 'keydown', 'pointerdown'] as const;

// The primary-outlet path without matrix or query params. Drawers, the month selector,
// and list filters only change params, so they stay on the same page.
export function routePath(serializer: UrlSerializer, url: string): string {
  const segments = serializer.parse(url).root.children[PRIMARY_OUTLET]?.segments ?? [];
  return segments.map(segment => encodeURIComponent(segment.path)).join('/');
}

// Angular's scrollPositionRestoration scrolls to the top on every navigation, including
// param-only ones. Requires withInMemoryScrolling() so the router emits Scroll events.
export function provideRouteScrollReset(): EnvironmentProviders {
  return provideEnvironmentInitializer(() => {
    const router = inject(Router);
    const serializer = inject(UrlSerializer);
    const scroller = inject(ViewportScroller);
    const view = inject(DOCUMENT).defaultView;
    let previousPath: string | undefined;
    let cancelRestore = () => {};

    // Pages render their data after the Scroll event, so a saved position can be taller
    // than the page at first. Keep reapplying it as the page grows, until it sticks,
    // the user scrolls, or the next navigation starts.
    const restore = (position: [number, number]) => {
      const reached = () => {
        const [x, y] = scroller.getScrollPosition();
        return Math.abs(x - position[0]) < 1 && Math.abs(y - position[1]) < 1;
      };

      scroller.scrollToPosition(position, { behavior: 'instant' });
      if (reached() || !view || typeof ResizeObserver === 'undefined') return;

      const observer = new ResizeObserver(() => {
        scroller.scrollToPosition(position, { behavior: 'instant' });
        if (reached()) cancelRestore();
      });
      const stop = () => cancelRestore();
      const timer = view.setTimeout(stop, RESTORE_TIMEOUT_MS);

      observer.observe(view.document.body);
      USER_SCROLL_EVENTS.forEach(type => view.addEventListener(type, stop, { passive: true }));
      cancelRestore = () => {
        observer.disconnect();
        view.clearTimeout(timer);
        USER_SCROLL_EVENTS.forEach(type => view.removeEventListener(type, stop));
        cancelRestore = () => {};
      };
    };

    // Own scroll restoration only for in-app navigations. Handing it back to the browser
    // on pagehide keeps native restoration for reloads and cross-document history.
    const releaseRestoration = () => scroller.setHistoryScrollRestoration('auto');
    view?.addEventListener('pagehide', releaseRestoration);

    const subscription = router.events.subscribe(event => {
      if (event instanceof NavigationStart) {
        cancelRestore();
        // The initial navigation is the document load, which the browser restores itself.
        if (event.id > 1) scroller.setHistoryScrollRestoration('manual');
        return;
      }
      if (!(event instanceof Scroll) || !(event.routerEvent instanceof NavigationEnd)) return;

      const path = routePath(serializer, event.routerEvent.urlAfterRedirects);
      const pathChanged = previousPath !== undefined && path !== previousPath;
      previousPath = path;

      if (event.scrollBehavior === 'manual') return;
      if (event.position) restore(event.position);
      else if (pathChanged) scroller.scrollToPosition([0, 0], { behavior: 'instant' });
    });

    inject(DestroyRef).onDestroy(() => {
      subscription.unsubscribe();
      cancelRestore();
      view?.removeEventListener('pagehide', releaseRestoration);
    });
  });
}
